import { FormElementProps } from 'components/common/form/index';
import { FormComponentValue } from 'components/common/form/layout/control';
import { useContext, useEffect, useRef, useState } from 'react';
import { FormTabType, TabContext } from 'components/common/form/tab';
import { useFormState } from 'react-hook-form';
import { FormsContext, FormsContextType } from 'components/common/form/hooks/useFormContext';
import { useSelector } from 'react-redux';
import { formSliceState } from 'store/formSlice';
import { applyComponentValuesCondition } from 'components/common/form/dependency/applyComponentValuesCondition';
import applyComponentCondition from 'components/common/form/dependency/applyComponentCondition';
import { DependencyRule, FormComponentDependency, getRuleFieldName } from 'components/common/form/dependency';

export default function useFormDependency(props: FormElementProps, componentStartValues: Array<FormComponentValue>) {
    const { form, component } = { form: props.form, component: props.component };

    const tabContext = useContext<FormTabType | null>(TabContext);
    const contextProps: FormTabType = tabContext?.componentsProps ?? {};
    const { errors } = useFormState({ control: form.hookFormControl, name: component.name });
    const formsContext = useContext<FormsContextType>(FormsContext);
    const isDirty = form.hookFormState.isDirty;

    // Element initial value (Used to restore Select value after values list reload)
    const [initialValue] = useState(form.hookFormGetValues(component.name));

    const emptySharedState = useRef({});
    const sharedState: any = useSelector((state: any) => {
        const formData = (state?.form as formSliceState).formElementsSharedState[props.form.formKey];
        if (formData && typeof formData[props.component.name] != 'undefined') {
            if (Object.keys(formData[props.component.name]).length === 0) {
                return emptySharedState.current;
            }
            return formData[props.component.name];
        }
        return emptySharedState.current;
    });
    const componentStatePropsRef = useRef<any>(sharedState);

    useEffect(() => {
        if (JSON.stringify(componentStatePropsRef.current) == JSON.stringify(sharedState)) {
            return;
        }
        componentStatePropsRef.current = sharedState;
        // This will not change current value, but trigger 'hookFormWatch' in depending fields
        props.form.hookFormSetValue(props.component.name, props.form.hookFormGetValues(props.component.name));
    }, [sharedState]);

    // Calculate Values 'props' depending on other fields values
    const [componentValues, setComponentValues] = useState<Array<FormComponentValue>>([]);
    const componentValuesRef = useRef<any>(componentValues);
    useEffect(() => {
        if (JSON.stringify(componentValuesRef.current) == JSON.stringify(componentValues)) {
            return;
        }
        componentValuesRef.current = componentValues;

        // After values list update, reset current value if it not in new list
        let currentValue = form.hookFormGetValues(component.name);
        // TODO: change FormSelect...
        if (form.formDidMount && component.component == 'FormSelect' && componentProps?.isMulti != true) {
            //ToDo empty value as null from back changed to first option. Example glossary_term_status_id. Need investigate
            const valueFound = componentValues.find((v) => v.value == currentValue);
            const prevValueFound = componentValues.find((v) => v.value == initialValue);
            // If current value not present in list try to restore initialValue
            if (!valueFound || currentValue == '' || currentValue == '0') {
                if (!prevValueFound) {
                    // If both current and initial values are missing then set first available value
                    currentValue = componentValues[0]?.value ?? '';
                } else {
                    // Restore initial value
                    currentValue = initialValue;
                }
            }
        }
        // Update value and/or (even if values was not changed) trigger 'hookFormWatch' in depending fields
        form.hookFormSetValue(component.name, currentValue);
    }, [componentValues]);

    useEffect(() => {
        setComponentValues(applyComponentValuesCondition(props, componentStartValues, formsContext));
    }, [componentStartValues]);

    // Calculate component 'props' depending on other fields values
    const [componentProps, setComponentProps] = useState<any>(
        applyComponentCondition(props, formsContext, contextProps),
    );
    useEffect(() => {
        // Should fire only ones, after form mount.
        // If field has some deps, will call updateComponentProps for update
        if (form.formDidMount) {
            setComponentProps(applyComponentCondition(props, formsContext, contextProps));
        }
    }, [form.formDidMount, JSON.stringify(props.componentProps)]);

    // Notify other fields about 'props' change
    const componentPropsRef = useRef<any>(componentProps);
    useEffect(() => {
        if (JSON.stringify(componentPropsRef.current) == JSON.stringify(componentProps)) {
            return;
        }
        componentPropsRef.current = componentProps;
        // This will not change current value, but trigger 'hookFormWatch' in depending fields
        form.hookFormSetValue(component.name, form.hookFormGetValues(component.name));
    }, [componentProps]);

    const updateComponentProps = function (newProps: any) {
        // Prop with name '$value' act as real field value
        if (typeof newProps.$value != 'undefined') {
            if (form.hookFormGetValues(component.name) != newProps.$value) {
                form.hookFormSetValue(component.name, newProps.$value);
                if (errors[component.uid]) {
                    form.hookFormClearErrors(component.name);
                }
            }
            delete newProps.$value;
        }
        setComponentProps(newProps);
    };

    // Subscribe component to changes in other fields values
    useEffect(() => {
        if (!form.formDidMount || !component.deps.length) {
            return;
        }

        const fieldsToWatchControl: Array<string> = [];
        const fieldsToWatchValue: Array<string> = [];

        component.deps.forEach((dep: FormComponentDependency) => {
            dep.rules.forEach((rule: DependencyRule) => {
                switch (dep.scope) {
                    case 'control':
                        fieldsToWatchControl.push(getRuleFieldName(rule.field));
                        break;
                    case 'value':
                        fieldsToWatchValue.push(getRuleFieldName(rule.field));
                        break;
                }
            });
            // Process initial form field values
            if (fieldsToWatchControl.length) {
                updateComponentProps(applyComponentCondition(props, formsContext, contextProps));
            }
            if (fieldsToWatchValue.length) {
                setComponentValues(applyComponentValuesCondition(props, componentStartValues, formsContext));
            }
        });

        const checkDepsRules = function (name: string) {
            if (fieldsToWatchControl.includes(name)) {
                updateComponentProps(
                    applyComponentCondition(props, formsContext, contextProps, formTriggerSaveRef.current),
                );
            }
            if (fieldsToWatchValue.includes(name)) {
                setComponentValues(applyComponentValuesCondition(props, componentStartValues, formsContext));
            }
        };

        const subscription = form.hookFormWatch((value, { name, type }) => {
            checkDepsRules(String(name));
        });

        return () => subscription.unsubscribe();
    }, [form.hookFormWatch, form.formDidMount, componentStartValues, isDirty]);

    // Check for events
    const [formTriggerSave, setFormTriggerSave] = useState<number>(0);
    const formTriggerSaveRef = useRef<number>(0);
    useEffect(() => {
        const triggerSave = form.triggerSave?.current ?? 0;
        if (triggerSave > 0) {
            updateComponentProps(
                applyComponentCondition(props, formsContext, contextProps, formTriggerSaveRef.current),
            );
            setFormTriggerSave(triggerSave);
        }
    }, [form.triggerSave?.current]);
    useEffect(() => {
        formTriggerSaveRef.current = formTriggerSave;
    }, [formTriggerSave]);

    useEffect(() => {
        form.addElementValuesRegister(props, componentValuesRef, componentPropsRef, componentStatePropsRef);
    }, []);

    return { componentProps, componentValues };
}
